import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { useTranslation } from "react-i18next";
import Outline from "../Outline/Outline";
import CourseActivityIcon from "../Icons/CourseActivityIcon";
import ResourceTypeIcon from "../Icons/ResourceTypeIcon";
import HTMLEditor from "../HtmlEditor/HtmlEditor";
import { Field, useFormikContext } from "formik";
import { CheckboxWithLabel } from "formik-mui";

const courseOverviewCharacterLimit = 800;

const CourseOutline = ({
  courseContent,
  resources,
  brsp,
  hideOverviewCheckbox,
  overviewCheckboxDisabled,
  onFilesListChange,
  filesList,
  disableFields
}) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const [courseContentOutline, setCourseContentOutline] = useState([]);
  const [resourcesOutline, setResourcesOutline] = useState([]);
  const { values, errors, setFieldValue, isSubmitting } = useFormikContext();

  useEffect(() => {
    let outline = courseContent
      ? courseContent.map(section => ({
          title: section.title,
          items: section.items.map(item => ({
            title: item.title,
            icon: <CourseActivityIcon type={item.type} />
          }))
        }))
      : [];
    setCourseContentOutline(outline);
  }, [courseContent]);

  useEffect(() => {
    const outline = resources
      ? resources.map(section => ({
          title: section.title,
          items: section.items.map(item => ({
            title: item.title,
            icon: <ResourceTypeIcon filled={true} type={item.type} />
          }))
        }))
      : [];
    setResourcesOutline(outline);
  }, [resources]);

  const handleNumCharactersChange = numCharacters => {
    setFieldValue("overviewTextOnlyLength", numCharacters);
  };

  return (
    <Box display="flex" flexDirection="column">
      {!hideOverviewCheckbox && (
        <Field
          component={CheckboxWithLabel}
          name="showOverview"
          inputProps={{
            "data-qa": "show-course-overview-checkbox"
          }}
          Label={{
            label: (
              <Typography variant="subtitle1">
                {t("manageCourses.showCourseOverviewLabel")}
              </Typography>
            )
          }}
          disabled={overviewCheckboxDisabled || disableFields}
          color="primary"
          type="checkbox"
        />
      )}
      <Box display="flex" my={3}>
        <Typography variant="h5">
          {t("courseVersion.details.courseOutline")}
        </Typography>
      </Box>
      <Tabs
        value={currentTab}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        aria-label="Course Outline"
        onChange={(ev, newTab) => setCurrentTab(newTab)}
      >
        <Tab key={0} label={t("courseVersion.details.tableContents")} />
        <Tab key={1} label={t("courseVersion.details.resources")} />
      </Tabs>
      {!brsp ? (
        <Box mt={4}>
          {values.showOverview && (
            <Box
              display="flex"
              flexDirection="column"
              pb={3}
              data-qa="course-overview-field"
            >
              <Typography variant="h6" sx={{ pb: 2 }}>
                {t("manageCourses.courseOverviewHeader")}
              </Typography>
              <HTMLEditor
                disableMargin
                onChange={value => setFieldValue("overviewText", value, false)}
                value={values.overviewText}
                menubar={false}
                height={200}
                onFileAdd={(fileObj, tempLink, type) => {
                  let list = filesList;
                  list.push({ fileObj, tempLink, type });
                  onFilesListChange(list);
                }}
                plugins={["lists", "link", "image", "media", "code"]}
                toolbar="bold italic underline | alignment numlist bullist outdent indent | link image media fileUpload"
                disableResize
                characterLimit={courseOverviewCharacterLimit}
                errorMessage={errors.overviewText}
                disabled={isSubmitting || disableFields}
                onNumCharactersChange={handleNumCharactersChange}
              />
            </Box>
          )}
          <Outline
            sections={
              currentTab === 0 ? courseContentOutline : resourcesOutline
            }
          />
        </Box>
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" mt={5}>
          <Typography textColor="primary">
            {t("courseVersion.details.messageForBrspVersions")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CourseOutline;

CourseOutline.propTypes = {
  courseContent: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string,
          title: PropTypes.string
        })
      )
    })
  ),
  resources: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string,
          title: PropTypes.string
        })
      )
    })
  ),
  showOverviewCheckbox: PropTypes.bool
};
